import type { KeyboardEvent, RefObject } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Dropdown, Input, Modal, Switch } from 'antd';
import type { InputRef, MenuProps } from 'antd';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import FormOutlined from '@ant-design/icons/FormOutlined';
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import { useTranslation } from 'react-i18next';
import ShellFooter from '@/features/shell/ShellFooter';
import { useRunStore } from '../runs/runStore';
import { groupInquiries } from './groupInquiries';
import styles from './history.module.css';

export interface RecentInquiry {
  id: string | number;
  name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  owner_name?: string | null;
  is_own?: boolean;
  /** The run queued, running or stopping in it (chat-runs-api.md §15). */
  active_run?: {
    id: string;
    status: string;
    stage: string | null;
    /** Who started it (API prompt 17). */
    user_id?: number | null;
  } | null;
  /** A clarification waits for this user's reply (chat-runs-api.md §17.7). */
  needs_reply?: boolean;
}

interface RecentQAPanelProps {
  open: boolean;
  /** Below 640 px the panel is a modal dialog; otherwise it overlays the chat. */
  modal: boolean;
  onClose: () => void;
  /** Focus returns here when the panel closes. */
  returnFocusRef: RefObject<HTMLElement>;
  inquiries: RecentInquiry[];
  selectedId: string | number | null | undefined;
  /** Last activity seen in this session (ms), per inquiry id. */
  activity: Record<string, number>;
  onSelect: (inquiry: RecentInquiry) => void;
  onNew: () => void;
  onRename: (inquiry: RecentInquiry) => void;
  onExport: (inquiry: RecentInquiry) => void;
  onDelete: (inquiry: RecentInquiry) => void;
  isAdmin: boolean;
  orgView: boolean;
  onOrgViewChange: (value: boolean) => void;
}

/**
 * Recent Q&As (spec §2, §3): a 320 px panel over the workspace that never
 * pushes it aside (a modal below 640 px), with search, the mutually exclusive
 * Today / Yesterday / Previous 7 days / Older groups, Generating and New
 * badges, and rename, export and delete per Q&A.
 */
const RecentQAPanel = ({
  open,
  modal,
  onClose,
  returnFocusRef,
  inquiries,
  selectedId,
  activity,
  onSelect,
  onNew,
  onRename,
  onExport,
  onDelete,
  isAdmin,
  orgView,
  onOrgViewChange,
}: RecentQAPanelProps) => {
  const { t } = useTranslation();
  const runs = useRunStore();
  const [query, setQuery] = useState('');
  const search = useRef<InputRef>(null);
  const titleId = useId();
  const wasOpen = useRef(false);

  const groups = useMemo(
    () => groupInquiries(inquiries, Date.now(), query, activity),
    [inquiries, query, activity],
  );

  // Focus the search field on open; give focus back to the toggle on close.
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      const id = requestAnimationFrame(() => search.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      returnFocusRef.current?.focus();
    }
    return undefined;
  }, [open, returnFocusRef]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.stopPropagation();
      onClose();
    }
  };

  const menu = (inquiry: RecentInquiry): MenuProps => {
    const canEdit = !orgView || inquiry.is_own === true;
    const items: MenuProps['items'] = [
      ...(canEdit
        ? [
            {
              key: 'rename',
              icon: <EditOutlined aria-hidden />,
              label: t('v2.common.rename'),
              onClick: () => onRename(inquiry),
            },
          ]
        : []),
      {
        key: 'export',
        icon: <DownloadOutlined aria-hidden />,
        label: t('v2.common.exportCsv'),
        onClick: () => onExport(inquiry),
      },
      ...(canEdit
        ? [
            { type: 'divider' as const },
            {
              key: 'delete',
              icon: <DeleteOutlined aria-hidden />,
              label: t('v2.common.del'),
              danger: true,
              onClick: () => onDelete(inquiry),
            },
          ]
        : []),
    ];
    return { items };
  };

  const list = (
    <div className={styles.body}>
      <button
        type="button"
        className={styles.newButton}
        onClick={() => {
          onNew();
          onClose();
        }}
      >
        <FormOutlined aria-hidden />
        <span>{t('v2.history.newQA')}</span>
      </button>
      <div className={styles.search}>
        <Input
          ref={search}
          type="search"
          allowClear
          prefix={<SearchOutlined aria-hidden />}
          placeholder={t('v2.history.searchHistory')}
          aria-label={t('v2.history.searchHistory')}
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
      </div>
      {isAdmin && (
        <label className={styles.orgView}>
          <Switch
            size="small"
            checked={orgView}
            onChange={onOrgViewChange}
            aria-label={t('v2.history.orgView')}
          />
          <span>{t('v2.history.orgView')}</span>
        </label>
      )}
      {groups.length === 0 ? (
        <div role="status" className={styles.empty}>
          {query.trim() ? (
            <>
              <span>{t('v2.history.noHistoryMatch', { q: query.trim() })}</span>
              <button
                type="button"
                className={styles.textButton}
                onClick={() => setQuery('')}
              >
                {t('v2.history.clearSearch')}
              </button>
            </>
          ) : (
            <span>{t('v2.history.noHistory')}</span>
          )}
        </div>
      ) : (
        groups.map(({ group, items }) => (
          <section
            key={group}
            aria-labelledby={`${titleId}-${group}`}
            className={styles.group}
          >
            <h3 id={`${titleId}-${group}`} className={styles.groupTitle}>
              {t(`v2.history.${group}`)}
            </h3>
            <ul className={styles.items} data-testid={`recent-group-${group}`}>
              {items.map(inquiry => {
                const run = runs.get(inquiry.id);
                const selected =
                  String(inquiry.id) === String(selectedId ?? '');
                const name = inquiry.name || t('v2.history.untitled');
                return (
                  <li
                    key={inquiry.id}
                    className={styles.row}
                    data-selected={selected}
                  >
                    <button
                      type="button"
                      className={styles.rowMain}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => {
                        onSelect(inquiry);
                        if (modal) onClose();
                      }}
                      data-testid="recent-item"
                      data-inquiry-id={inquiry.id}
                    >
                      <span className={styles.rowName} title={name}>
                        {name}
                      </span>
                      {orgView && inquiry.owner_name ? (
                        <span className={styles.rowMeta}>
                          {t('v2.history.createdBy', {
                            name: inquiry.owner_name,
                          })}
                        </span>
                      ) : null}
                    </button>
                    {run.generating ||
                    (inquiry.active_run &&
                      !runs.getRun(inquiry.active_run.id)) ? (
                      <span
                        className={`${styles.badge} ${styles.badgeRunning}`}
                        data-testid="badge-generating"
                      >
                        {t('v2.history.running')}
                      </span>
                    ) : inquiry.needs_reply ? (
                      <span
                        className={`${styles.badge} ${styles.badgeNeedsReply}`}
                        data-testid="badge-needs-reply"
                      >
                        {t('v2.history.needsReply')}
                      </span>
                    ) : run.unread ? (
                      <span
                        className={`${styles.badge} ${styles.badgeNew}`}
                        data-testid="badge-new"
                      >
                        {t('v2.history.unread')}
                      </span>
                    ) : null}
                    <Dropdown
                      menu={menu(inquiry)}
                      trigger={['click']}
                      placement="bottomRight"
                    >
                      <button
                        type="button"
                        className={styles.more}
                        aria-label={t('v2.common.moreFor', { name })}
                        title={t('v2.common.more')}
                        data-testid="inquiry-actions"
                      >
                        <MoreOutlined aria-hidden />
                      </button>
                    </Dropdown>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  );

  if (modal) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        title={t('v2.history.recent')}
        footer={<ShellFooter />}
        className={`${styles.modal} rqa-drawer`}
        closeIcon={<CloseOutlined aria-label={t('v2.history.recentClose')} />}
      >
        {list}
      </Modal>
    );
  }

  if (!open) return null;
  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      className={styles.panel}
      onKeyDown={onKeyDown}
      data-testid="recent-panel"
    >
      <div className={styles.panelHeader}>
        <h2 id={titleId} className={styles.panelTitle}>
          {t('v2.history.recent')}
        </h2>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t('v2.history.recentClose')}
          title={t('v2.history.recentClose')}
          onClick={onClose}
        >
          <CloseOutlined aria-hidden />
        </button>
      </div>
      <div className={styles.scroll}>{list}</div>
      <ShellFooter />
    </section>
  );
};

export default RecentQAPanel;
